import { VersionedViewModelBase } from '@/components/basedata/VersionedViewModelBase'
import ImageVault from '@/data/ImageVaultData'
import { categoriesMapper } from '@/store/modules/categories'
import { Category, CategoryLgVersion } from '@/types/categories'
import { Subcategory, SubcategoryLgVersion } from '@/types/subcategories'
import { localeSort } from '@/utils/formatting'
import { getLgVersion } from '@/utils/language'

export interface LookupItem {
  id: number
  name: string
}

export default VersionedViewModelBase<Subcategory, SubcategoryLgVersion>().extend({
  name: 'SubcategoryViewModel',

  data: () => {
    return {}
  },

  computed: {
    ...categoriesMapper.mapState({
      allCategories: 'categories'
    }),

    isNew (): boolean {
      return !this.model.subcategoryId
    },

    // Model:
    subcategoryId: {
      get (): number {
        return this.model.subcategoryId
      },
      set (value: number) {
        this.model.subcategoryId = value
      }
    },
    categoryId: {
      get (): number {
        return this.model.categoryId
      },
      set (value: number) {
        this.model.categoryId = value
      }
    },
    defaultStoreMainImageVaultId: {
      get (): number | null {
        return this.model.defaultStoreMainImageVaultId
      },
      set (value: number | null) {
        this.model.defaultStoreMainImageVaultId = value

        ImageVault.updateUrlFromImageVaultIdDebounced(
          this.model.defaultStoreMainImageVaultId,
          url => (this.model.defaultStoreMainImageUrl = url),
          error =>
            this.$snackbar.show(
              'Kunne ikke hente url for ImageVault-ID: ' + this.defaultStoreMainImageVaultId,
              'error'
            ),
          id => this.$snackbar.show('Ukjent ImageVault-ID: ' + id, 'warning')
        )
      }
    },
    defaultStoreMainImageUrl: {
      get (): string | null {
        return this.model.defaultStoreMainImageUrl
      },
      set (value: string | null) {
        this.model.defaultStoreMainImageUrl = value
      }
    },
    createdDate (): Date | undefined {
      return this.model.created
    },
    lastUpdatedDate (): Date | undefined {
      return this.model.created
    },

    // lgModel:
    name: {
      get (): string {
        return this.lgModel.name
      },
      set (value: string) {
        this.lgModel.name = value
      }
    },

    // Related:
    category (): Category | null {
      return this.model.categoryId
        ? this.allCategories.find(x => x.categoryId == this.model.categoryId)!
        : null
    },
    categoryUiVersion (): CategoryLgVersion | null {
      return this.category ? getLgVersion<CategoryLgVersion>(this.category, this.$uiLanguage) : null
    },
    categoryUiName (): string | null {
      return this.categoryUiVersion ? this.categoryUiVersion.name : null
    },

    // Lookups:
    categoryLookups (): LookupItem[] {
      return this.allCategories
        .map(x => ({
          id: x.categoryId,
          name: getLgVersion<CategoryLgVersion>(x, this.$uiLanguage).name
        }))
        .sort((a, b) => localeSort(a.name, b.name))
    }
  },

  methods: {
    ...categoriesMapper.mapActions({
      loadCategories: 'loadCategories'
    }),

    // Mappings:
    mapToCategories (categories: number[]): Category[] {
      return categories.map(categoryId => this.allCategories.find(x => x.categoryId == categoryId)!)
    },
    mapToCategoryLookups (categories: number[]): LookupItem[] {
      return categories.map(x => this.categoryLookups.find(y => y.id == x)!)
    }
  },

  mounted () {
    this.loadCategories({ ensureOnly: true }).catch(error => {
      console.log('Kunne ikke laste kategorier: ' + error)
      if (this.allCategories.length > 0) console.log('Viser bufrede data.')
    })
  }
})
